import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useHistory } from 'react-router'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { Body } from 'components/Shell/Shell'
import OfficeHeaderTitle from 'components/OfficeHeaderTitle/OfficeHeaderTitle'
import { DetailCard } from '@tecuity/granite-ui/DetailCard'
import { Detail } from '@tecuity/granite-ui/Detail'
import { IconButton } from '@tecuity/granite-ui/IconButton'
import format from 'date-fns/format'
import './Correspondence.scss'
import { SET_CORRESPONDENCE2_REDUCER, SET_CORRESPONDENCE_RECIPIENT } from './Correspondence2Reducer'
import { Spinner } from 'components/Loaders/Loaders'
import { checkDLNValid } from 'components/BOSearch/BOSearchActions'
import { openSpecificImage } from 'components/ImageViewer/ImageViewerActions'
import { setErrorToast, setSuccessToast } from 'components/../actions'
import { useRequestData } from 'components/../Utilities/hooks'
import { GET_ACTIVE_BASIC_LOOKUP_TYPES } from 'components/../requests'
import CorrWizard from './Steps/CorrWizard'
import * as api from '../../requests'
import FileDownloader from 'components/ImageViewer/FileDownloader'
import DownloadBox from '@tecuity/sos-icons/react/download-box'
import { base64ToArrayBuffer } from '../Accounting/ReceiptValidation/ReceiptValidationActions'
import { getUSADefault } from '../Wizard2/selectOptions'
import { getImageDataTypeFromDLN } from '../WorkviewManager/WorkviewActions'

const Correspondence = () => {
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const processingStatusOptions = useRequestData(
    (cb, errCb) => GET_ACTIVE_BASIC_LOOKUP_TYPES('', 'ProcessingStatusType', cb, errCb),
    [],
    []
  )
  const [downloadURL, setDownloadURL] = useState(null)
  const [showDownloadReportButton, setShowDownloadReportButton] = useState(false)
  const [downloadingReport, setDownloadingReport] = useState(false)
  const downloader = useRef(null)
  const { FIRST_NAME, LAST_NAME } = useSelector((state) => state.auth, shallowEqual)
  const corrInfo = useSelector((state) => state.correspondence2, shallowEqual)
  const {
    CustomCorrespondenceNumber,
    CustomCorrespondenceTypeId,
    CustomCorrespondenceTypeName,
    EntryType,
    FilingDate,
    FilingType,
    ImageDataType,
    ImageNum,
    LastModifiedByName,
    ProcessingStatusType,
    RecordName,
    RecordType,
    SourceNumber,
    SourceTypeId,
    sourceTypes,
    loading,
    workviewItem,
    searchItem,
  } = corrInfo

  useEffect(() => {
    if (workviewItem && workviewItem.WorkViewItemId) {
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          SourceTypeId: workviewItem.SourceTypeId,
          // SourceType: '', // handled with sourceTypes
          SourceId: workviewItem.Filing.Id,
          RecordTypeId: workviewItem.Record.TypeId || 0,
          RecordSubTypeId: workviewItem.Record.SubTypeId || null,
          FilingTypeId: workviewItem.Filing.TypeId,          
          MiscellaneousModuleId: workviewItem.MiscellaneousModuleId || null,
          RecordName: workviewItem.ResourceName,
          SourceNumber: workviewItem.Filing.Num,
          // ProcessingStatusTypeId: null, // handled by location
          // ProcessingStatusType: '', // handled by location
          FilingDate: format(new Date(), 'MM/dd/yyyy HH:mm a'),
          ImageNum: workviewItem.DLN,
          // EnclosedAmount: null, // not using yet
          // ReceiptNumber: null, // not using yet
          CreatedDate: new Date(),
          CreatedByAppUserId: workviewItem.AppUserID,
          LastModifiedDate: new Date(),
          LastModifiedBy: workviewItem.AppUserID,
          LastModifiedByName: `${FIRST_NAME} ${LAST_NAME}`,
          EntryTypeId: workviewItem.EntryTypeId,
          EntryType: workviewItem.EntryTypeName,
          ImageGroupId: workviewItem.ImageGroupId,
          ImageDataType: workviewItem.ResourceImagePageType,
          Email: workviewItem.EntryTypeName !== 'Paper Filed' ? workviewItem.SubmitterEmail : '',
          isEmail: workviewItem.EntryTypeName !== 'Paper Filed' ? true : false,
          isSnailMail: workviewItem.EntryTypeName === 'Paper Filed',
        },
      })
      dispatch({
        type: SET_CORRESPONDENCE_RECIPIENT,
        accessor: 'Country',
        value: getUSADefault(),
      })
      if (workviewItem.Filing.Num && workviewItem.SourceTypeId)
        api.FETCH_WEB_USER_EMAIL_FROM_FILING_NUM(
          workviewItem.Filing.Num,
          workviewItem.SourceTypeId,
          (data) => {
            dispatch({ type: SET_CORRESPONDENCE_RECIPIENT, accessor: 'Email', value: data })
          },
          (err) => console.error(err)
        )
    }
    return () => {
      if (workviewItem) {
        api.UNLOCK_WORKVIEW_ITEM_BY_ID(
          workviewItem.WorkViewItemId,
          () => {
            dispatch(setSuccessToast('Unlocked Work Item'))
          },
          (err) => {
            console.error(err)
            // dispatch(setErrorToast('Failed to Unlock Work Item'))
          }
        )
      }
    }
  }, [workviewItem])

  useEffect(() => {
    if (searchItem && (searchItem['Correction #'] || searchItem['Rejection #'])) {
      dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { loading: true, readOnly: true } })
      api.GET_EXISTING_CORRESPONDENCE(
        searchItem['Correction #'] || searchItem['Rejection #'],
        (data) => {
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: {
              ...data,
              CustomCorrespondenceReasons: data.CustomCorrespondenceReasons.map((x) => ({
                ...x,
                parsedReason: x.LongDescription,
              })),
              isEmail: !!data.CustomCorrespondenceRecipient.Email,
              isSnailMail: !!data.CustomCorrespondenceRecipient.Address1,
              CustomCorrespondenceTypeName: data.CustomCorrespondenceTypeId === 1 ? 'Rejection' : 'Correction',
            },
          })
          api.GET_CORRESPONDENCE_REASONS_BY_CUSTOM_CORRESPONDENCE_ID(
            data.CustomCorrespondenceId,
            (reasonsRtn) => {
              let fullGeneralReasons = reasonsRtn.GeneralReasons.map((ctg) => {
                let newReasons = ctg.Reasons.map((rsn) => {
                  let existingReason = data.CustomCorrespondenceReasons.find(
                    (x) => x.CustomCorrespondenceReasonTypeId === rsn.ID
                  )
                  let ParamList = []
                  if (existingReason.ParamList) {
                    const keys = Object.keys(existingReason.ParamList)
                    keys.forEach((key) => {
                      ParamList.push({ value: existingReason.ParamList[key], key })
                    })
                  }
                  return {
                    ...rsn,
                    parent: 'GeneralReasons',
                    checked: true,
                    paramsList: ParamList.length > 0 ? ParamList : undefined,
                    parsedReason: existingReason.LongDescription
                      ? existingReason.LongDescription
                      : undefined,
                  }
                })
                return { ...ctg, isOpen: true, Reasons: newReasons }
              })
              let fullSpecificReasons = reasonsRtn.SpecificReasons.map((ctg) => {
                let newReasons = ctg.Reasons.map((rsn) => {
                  let existingReason = data.CustomCorrespondenceReasons.find(
                    (x) => x.CustomCorrespondenceReasonTypeId === rsn.ID
                  )
                  let ParamList = []
                  if (existingReason.ParamList) {
                    const keys = Object.keys(existingReason.ParamList)
                    keys.forEach((key) => {
                      ParamList.push({ value: existingReason.ParamList[key], key })
                    })
                  }
                  return {
                    ...rsn,
                    parent: 'SpecificReasons',
                    checked: true,
                    paramsList: ParamList.length > 0 ? ParamList : undefined,
                    parsedReason: existingReason.LongDescription
                      ? existingReason.LongDescription
                      : undefined,
                  }
                })
                return { ...ctg, isOpen: true, Reasons: newReasons }
              })
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: {
                  generalReasons: fullGeneralReasons,
                  specificReasons: fullSpecificReasons,
                  loading: false,
                },
              })
            },
            (err) => {
              console.error(err)
            }
          )
          // to populate ImageDataType if applicable
          if (data.ImageNum)
            getImageDataTypeFromDLN(data.ImageNum, (imageDataType) => {
              if (imageDataType) {
                dispatch({
                  type: SET_CORRESPONDENCE2_REDUCER,
                  payload: {
                    ImageDataType: imageDataType,
                  },
                })
              }
            })
        }
      )
    }
  }, [searchItem])

  useEffect(() => {
    if ((!workviewItem || !workviewItem.WorkViewItemId) && (!searchItem || !searchItem.SourceId)) {
      history.goBack()
    }
  }, [workviewItem, searchItem])

  useEffect(() => {
    const isReject = location.pathname.includes('reject')
    const rejectionStatusType = processingStatusOptions.find((type) => type.Name === 'Rejected')
    const correctionStatusType = processingStatusOptions.find(
      (type) => type.Name === 'Needs Correction'
    )
    if (!searchItem) {
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          CustomCorrespondenceTypeId: isReject ? 1 : 2,
          CustomCorrespondenceTypeName: isReject ? 'Rejection' : 'Correction',
          ProcessingStatusTypeId: isReject
            ? rejectionStatusType
              ? rejectionStatusType.Id
              : 540
            : correctionStatusType
              ? correctionStatusType.Id
              : 559,
          ProcessingStatusType: corrInfo.ProcessingStatusType
            ? corrInfo.ProcessingStatusType
            : isReject
              ? 'Rejected'
              : 'Needs Correction',
        },
      })
    }
  }, [location, processingStatusOptions, searchItem])

  useEffect(() => {
    if (!SourceTypeId || sourceTypes.length === 0) {
      return
    }
    const option = sourceTypes.find((opt) => {
      return +opt.Id === +SourceTypeId
    })
    const descr = option ? option.Name : ''
    dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { SourceType: descr } })
  }, [SourceTypeId, sourceTypes])

  useEffect(() => {
    if (searchItem && searchItem.SourceId && location.pathname) {
      setShowDownloadReportButton(true)
    }
    else {
      setShowDownloadReportButton(false)
    }
  }, [searchItem, location.pathname])

  const downloadReport = () => {
    setDownloadingReport(true)
    api.DOWNLOAD_CUSTOM_CORRESPONDENCE_PDF(
      location.pathname.includes('reject') ? 1 : 2,
      searchItem.SourceId,
      (data) => {
        const arrayBuffer = base64ToArrayBuffer(data.PDF ? data.PDF : data)
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' })
        const link = window.URL.createObjectURL(blob)
        setDownloadURL(link)
        setDownloadingReport(false)
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to load report.'))
        setDownloadingReport(false)
      }
    )
  }

  useEffect(() => {
    if (downloadURL) {
      downloader.current.download()
    }
  }, [downloadURL])

  return (
    <Body className="correct-reject">
      <OfficeHeaderTitle>
        {CustomCorrespondenceTypeId === 2 ? 'Send for Correction' : 'Send Rejection'}
      </OfficeHeaderTitle>
      <div>
        <div className="info-row">
          <DetailCard
            label={RecordName || ImageDataType || 'Filing'}
            customCss={{
              backgroundColor: 'white',
              width: '100%',
            }}
          >
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '15px',
                  marginBottom: '15px',
                  paddingTop: '15px',
                }}
              >
                <Spinner size="medium" themeColor="dark-neutral" />
              </div>
            ) : (
              <div className="details-row">
                <div className="details-col">
                  <Detail label={'Correspondence Type'}>{CustomCorrespondenceTypeName}</Detail>
                  <Detail label={`${CustomCorrespondenceTypeName} Number`}>
                    {CustomCorrespondenceNumber}
                  </Detail>
                </div>

                <div className="details-col">
                  {RecordType && <Detail label="Record Type">{RecordType}</Detail>}
                  {FilingType && <Detail label="Filing Type">{FilingType}</Detail>}
                  {ImageNum && (
                    <Detail label="DLN #">
                      <span
                        className="dln-link"
                        onClick={() => {
                          checkDLNValid(
                            ImageNum,
                            (id) => {
                              dispatch(openSpecificImage(id, ImageNum))
                            },
                            (dln) => {
                              dispatch(setErrorToast('DLN ' + dln + ' not found'))
                            }
                          )
                        }}
                      >
                        {ImageNum}
                      </span>
                    </Detail>
                  )}
                </div>

                <div className="details-col">
                  {SourceNumber && <Detail label="Filing #">{SourceNumber}</Detail>}
                  {FilingDate && (
                    <Detail label={`Received Date`}>
                      {workviewItem
                        ? format(new Date(workviewItem.FileDate), 'MM/dd/yyyy')
                        : FilingDate.split(' ')[0]}
                      <br />
                      {workviewItem
                        ? format(new Date(workviewItem.FileDate), 'HH:mm a')
                        : FilingDate.split(' ').slice(1).join(' ')}
                    </Detail>
                  )}
                  {/* {corrInfo.ReceiptNumber && (
                  <Detail label="Receipt #">{corrInfo.ReceiptNumber}</Detail>
                )} */}
                  {/* {!!corrInfo.EnclosedAmount && (
                  <Detail label="Enclosed Amount">{`$${(+corrInfo.EnclosedAmount < Number(0)
                    ? +corrInfo.EnclosedAmount * -1
                    : +corrInfo.EnclosedAmount
                  )
                    .toFixed(2)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}</Detail>
                )} */}
                </div>

                <div className="details-col">
                  {EntryType && <Detail label="Submit Method">{EntryType}</Detail>}
                  {ImageDataType && <Detail label="Image Data Type">{ImageDataType}</Detail>}
                </div>

                <div className="details-col">
                  {ProcessingStatusType && <Detail label="Status">{ProcessingStatusType}</Detail>}
                  {LastModifiedByName && <Detail label="Reviewed By">{LastModifiedByName}</Detail>}
                  {showDownloadReportButton && (
                    <Detail label="Download">
                      {downloadingReport ?
                        <div style={{padding:"10px"}}><Spinner size="small" themeColor="dark-neutral" /></div>
                        : <span title="Download Report">
                          <IconButton
                            onClick={downloadReport}
                            icon={<DownloadBox />}
                            label="Download Report"
                            customCss={{
                              height: '35px',
                              width: '35px',
                              svg: { height: '30px', width: '30px', marginTop: '-7px' },
                            }}
                          />
                        </span>
                      }
                    </Detail>
                  )}
                </div>
              </div>
            )}
          </DetailCard>
        </div>
        <CorrWizard />
      </div>
      <FileDownloader
        fileName={`${CustomCorrespondenceTypeName}-${CustomCorrespondenceNumber}.pdf`}
        href={downloadURL}
        ref={downloader}
      />
    </Body>
  )
}

export default Correspondence
